import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import GlobalLoader from "../../common/GlobalLoader";
import CreateVideoPopup from "../../common/CreateVideoPopup";
import VideoCard from "../../common/VideoCard";
import ViewMaterialPopup from "../../common/ViewMaterialPopup";
import EmptyPage from "../../common/emptyPage";
import InstitutionCourseCard from "../institutionCourseCard";
import TeacherBatchCard from "../../teacher/homework/TeacherBatchCard";

import { getCourses } from "../../../helper/apicalls/course";
import { getAllBatchInstitution } from "../../../helper/apicalls/institution/institutionBatch";

import {
  deleteVideos,
  getAllVideos,
  updateVideos,
  uploadVideos,
} from "../../../helper/apicalls/videos";

import "../../../styles/admin/videos.scss";
import "../../../styles/institution/institutionBatchCourseList.scss";
import "../../../styles/teacher/homeworkBatches.scss";

import SuccessPopup from "../../common/SuccessPopup";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";

const VideosPage = () => {
  const { setHeaderName, setIsShowSearch, setReportBreadcrumbLinks } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const courseParam = queryParams.get("course");
  const batchParam = queryParams.get("batch");

  const [step, setStep] = useState("courses"); // "courses" | "batches" | "videos"
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loader, setLoader] = useState(false);

  const [isVideoPopupOpen, setIsVideoPopupOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);

  const [popupMode, setPopupMode] = useState("create");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeMaterial, setActiveMaterial] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const fetchCourses = async () => {
    try {
      setLoader(true);
      const data = await getCourses();
      const normalized = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data) && Array.isArray(data?.[0]?.course)
          ? data[0].course
          : Array.isArray(data)
            ? data
            : [];
      setCourses(normalized);
    } catch (err) {
      console.error("Error fetching courses in page:", err);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchCourses();
    return () => {
      if (typeof setReportBreadcrumbLinks === "function") {
        setReportBreadcrumbLinks(null);
      }
    };
  }, []);

  useEffect(() => {
    if (courses.length === 0) {
      setStep("courses");
      return;
    }

    const syncRouteState = async () => {
      if (courseParam && batchParam) {
        const foundCourse = courses.find(
          (c) => (c.id || c._id) === courseParam || c.key === courseParam
        );
        if (foundCourse) {
          setSelectedCourse(foundCourse);
          try {
            setLoader(true);
            const token = Cookies.get("institution_token");
            const response = await getAllBatchInstitution(token, foundCourse.key, {});
            const fetchedBatches = response?.data || [];
            setBatches(fetchedBatches);

            const foundBatch = fetchedBatches.find(
              (b) => (b.id || b._id) === batchParam
            );
            if (foundBatch) {
              setSelectedBatch(foundBatch);
            } else {
              setSelectedBatch({ id: batchParam, name: `Batch ${batchParam}` });
            }

            await getVideos(foundCourse.id || foundCourse._id, batchParam);
            setStep("videos");

            if (typeof setReportBreadcrumbLinks === "function") {
              setReportBreadcrumbLinks([
                {
                  label: "Courses",
                  path: "/teacher/videos",
                },
                {
                  label: foundCourse.name,
                  path: `/teacher/videos?course=${foundCourse.id || foundCourse._id}`,
                },
                {
                  label: foundBatch ? foundBatch.name : `Batch ${batchParam}`,
                  active: true,
                },
              ]);
            }
          } catch (err) {
            console.error("Error setting up batch & videos step:", err);
          } finally {
            setLoader(false);
          }
        } else {
          navigate("/teacher/videos", { replace: true });
        }
      } else if (courseParam) {
        const foundCourse = courses.find(
          (c) => (c.id || c._id) === courseParam || c.key === courseParam
        );
        if (foundCourse) {
          setSelectedCourse(foundCourse);
          setSelectedBatch(null);
          setVideos([]);
          try {
            setLoader(true);
            const token = Cookies.get("institution_token");
            const response = await getAllBatchInstitution(token, foundCourse.key, {});
            setBatches(response?.data || []);
            setStep("batches");

            if (typeof setReportBreadcrumbLinks === "function") {
              setReportBreadcrumbLinks([
                {
                  label: "Courses",
                  path: "/teacher/videos",
                },
                {
                  label: foundCourse.name,
                  active: true,
                },
              ]);
            }
          } catch (err) {
            console.error("Error setting up batches step:", err);
          } finally {
            setLoader(false);
          }
        } else {
          navigate("/teacher/videos", { replace: true });
        }
      } else {
        setStep("courses");
        setSelectedCourse(null);
        setSelectedBatch(null);
        setBatches([]);
        setVideos([]);

        if (typeof setReportBreadcrumbLinks === "function") {
          setReportBreadcrumbLinks([
            {
              label: "Courses",
              active: true,
            },
          ]);
        }
      }
    };

    syncRouteState();
  }, [location.search, courses]);

  useEffect(() => {
    setHeaderName("Videos");
    setIsShowSearch(false);
  }, []);

  const handleCourseClick = (course) => {
    navigate(`/teacher/videos?course=${course.id || course._id}`);
  };

  const handleBatchClick = (batch) => {
    navigate(`/teacher/videos?course=${selectedCourse?.id || selectedCourse?._id}&batch=${batch.id || batch._id}`);
  };

  const getVideos = async (courseVal = "", batchVal = "") => {
    try {
      setLoader(true);
      const response = await getAllVideos(courseVal, batchVal);
      if (response) setVideos(response);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoader(false);
    }
  };

  const handleSaveVideo = async (payload, mode, id) => {
    try {
      setLoader(true);
      if (mode === "edit") {
        await updateVideos(payload, id);
        setSuccessMsg("Video updated successfully!");
      } else {
        await uploadVideos(payload);
        setSuccessMsg("Video uploaded successfully!");
      }
      setIsVideoPopupOpen(false);
      setIsSuccessOpen(true);
      const courseVal = selectedCourse?.id || selectedCourse?._id || "";
      const batchVal = selectedBatch?.id || selectedBatch?._id || "";
      await getVideos(courseVal, batchVal);
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      setLoader(false);
    }
  };

  const triggerDeletePrompt = (video) => {
    setSelectedVideo(video);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleteOpen(false);
      setLoader(true);
      await deleteVideos(selectedVideo.id);
      setSuccessMsg("Video deleted successfully!");
      setIsSuccessOpen(true);
      const courseVal = selectedCourse?.id || selectedCourse?._id || "";
      const batchVal = selectedBatch?.id || selectedBatch?._id || "";
      await getVideos(courseVal, batchVal);
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setLoader(false);
      setSelectedVideo(null);
    }
  };

  const handlePlayVideo = (video) => {
    setActiveMaterial({
      content: video?.video_url,
      created_at: video?.created_at,
      id: video?.id,
      materials_type: "video",
      name: video?.title,
    });
  };

  return (
    <div className="videos-container">
      {loader && <GlobalLoader visible={loader} />}

      {/* Step 1: Course list */}
      {step === "courses" && (
        <div className="institution-batch-course-container" style={{ padding: 0 }}>
          <div className="institution-batch-course-header">
            <h4 className="section-title">Courses List</h4>
          </div>
          {Array.isArray(courses) && courses.length > 0 ? (
            <div className="institution-batch-course-grid" style={{ maxHeight: "none" }}>
              {courses.map((course) => (
                <InstitutionCourseCard
                  key={course.id || course._id || course.key}
                  course={course}
                  onClick={handleCourseClick}
                  hideMenuIcon={true}
                />
              ))}
            </div>
          ) : (
            <EmptyPage content={"No courses available"} />
          )}
        </div>
      )}

      {/* Step 2: Batch list */}
      {step === "batches" && (
        <div className="homework-batches-container" style={{ padding: 0 }}>
          <div className="homework-batches-header">
            <h2 style={{ margin: 0 }}>Batches List</h2>
          </div>
          {Array.isArray(batches) && batches.length > 0 ? (
            <div className="homework-batches-grid">
              {batches.map((batch, index) => (
                <TeacherBatchCard
                  key={batch.id || batch._id || index}
                  batch={batch}
                  onClick={() => handleBatchClick(batch)}
                />
              ))}
            </div>
          ) : (
            <EmptyPage content={"No batches available"} />
          )}
        </div>
      )}

      {/* Step 3: Videos grid */}
      {step === "videos" && (
        <div className="videos-grid">
          <div
            className="videos-add-card"
            onClick={() => {
              setPopupMode("create");
              setSelectedVideo(null);
              setIsVideoPopupOpen(true);
            }}
          >
            <div className="videos-add-card-body">
              <AddCircleOutlineOutlinedIcon className="videos-add-icon" />
              <h3>Upload Video</h3>
            </div>
          </div>

          {videos?.map((video) => (
            <VideoCard
              key={video.id}
              video={video}
              onEdit={() => {
                setPopupMode("edit");
                setSelectedVideo(video);
                setIsVideoPopupOpen(true);
              }}
              onPlay={() => handlePlayVideo(video)}
              onDelete={() => triggerDeletePrompt(video)}
            />
          ))}
        </div>
      )}

      {isVideoPopupOpen && (
        <CreateVideoPopup
          open={isVideoPopupOpen}
          handleClose={() => setIsVideoPopupOpen(false)}
          mode={popupMode}
          video={selectedVideo}
          onSave={handleSaveVideo}
          preselectedCourse={
            selectedCourse
              ? {
                  value: selectedCourse.id || selectedCourse._id,
                  label: selectedCourse.name,
                  key: selectedCourse.key,
                }
              : null
          }
          preselectedBatch={
            selectedBatch
              ? {
                  value: selectedBatch.id || selectedBatch._id,
                  label: selectedBatch.name,
                }
              : null
          }
        />
      )}

      {isDeleteOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isDeleteOpen}
          setIsConfirmDeleteOpen={setIsDeleteOpen}
          handleDelete={handleConfirmDelete}
          title="Delete Video"
          message={`Are you sure you want to delete "${selectedVideo?.title}"?`}
        />
      )}

      {isSuccessOpen && (
        <SuccessPopup
          open={isSuccessOpen}
          onClose={() => setIsSuccessOpen(false)}
          message={successMsg}
          type="success"
        />
      )}

      {activeMaterial && (
        <ViewMaterialPopup
          open={Boolean(activeMaterial)}
          handleClose={() => setActiveMaterial(null)}
          material={activeMaterial}
        />
      )}
    </div>
  );
};

export default VideosPage;
